import type { TypedDocumentNode, ApolloLink } from '@apollo/client';
import { gql } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useMutation } from '@apollo/client/react';
import { useCallback } from 'react';
import { useLocation } from 'react-router';

import type {
  Platform,
  SorarePrivateKeyAttributes,
  Sport,
} from '#src/__generated__/globalTypes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { currentUser } from '#src/contexts/currentUser/queries.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useAfterLoggedInTarget } from '#src/hooks/useAfterLoggedInTarget.ts';
import { useGetInteractionContext } from '#src/hooks/useGetInteractionContext.ts';
import { usePrevious } from '#src/hooks/usePrevious.ts';
import { useRedirectUrl } from '#src/hooks/useRedirectUrl.ts';
import { useSessionStorage } from '#src/hooks/useSessionStorage.ts';
import { useSkipOneDayEmailVerificationLatency } from '#src/hooks/useSkipOneDayEmailVerificationLatency.ts';
import { useUtmParams } from '#src/hooks/useUtmParams.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { getClientId } from '#src/lib/ga.ts';
import { formatGqlErrors } from '#src/lib/gql/index.ts';
import {
  importantNotificationCategories,
  notificationCategories,
} from '#src/lib/notifications.ts';
import { localStorage } from '#src/lib/storage/localStorage.ts';
import { SubmitSignUpForm } from '#src/protos/events/platform/web/events.ts';

import type {
  SignUpMutationWithUser,
  SignUpMutationWithUserVariables,
} from './__generated__/useSignUp.graphql.ts';

const SIGN_UP_MUTATION_WITH_USER = gql`
  mutation SignUpMutationWithUser(
    $input: signUpInput!
    $sport: Sport!
    $withCollectionPredictions: Boolean = false
    $notificationCategories: [NotificationCategoryInput!]
    $importantNotificationCategories: [NotificationCategoryInput!]
  ) {
    signUp(input: $input) {
      currentUser {
        slug
        nickname
        ...CurrentUserProvider_currentUser
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${currentUser}
` as TypedDocumentNode<SignUpMutationWithUser, SignUpMutationWithUserVariables>;

export const useSignUp = () => {
  const { updateQuery } = useConfigContext();
  const { sport } = useSportContext();
  const location = useLocation();
  const skipOneDayEmailVerificationLatency =
    useSkipOneDayEmailVerificationLatency();
  const lastLocation = usePrevious(location?.pathname);
  const redirectUrl = useRedirectUrl();

  const { getParams } = useUtmParams();
  const [signupPromo, setSignupPromo] = useSessionStorage('signup-promo');
  const { showNotification } = useSnackNotificationContext();
  const track = useEvents();
  const [mutate] = useMutation(SIGN_UP_MUTATION_WITH_USER);
  const afterLoggedInTarget = useAfterLoggedInTarget();
  const { recaptchaRef } = useConnectionContext();
  const getInteractionContext = useGetInteractionContext();

  const submit = useCallback(
    async (attributes: {
      nickname: string;
      email: string;
      passwordHash: string;
      certified: string;
      sorareAddress?: EthereumAddress;
      sorarePrivateKey?: SorarePrivateKeyAttributes;
      sorarePrivateKeyBackup?: string;
      agreedToReceiveOffersFromPartners: boolean;
      acceptTerms: boolean;
      acceptAgeLimit: boolean;
      acceptPrivacyPolicy: boolean;
      acceptGameRules: boolean;
      platform: Platform;
      signupSport: Sport;
      wallet: SignUpMutationWithUserVariables['input']['wallet'];
    }): Promise<ApolloLink.Result<SignUpMutationWithUser>> => {
      const { passwordHash, ...rest } = attributes;

      recaptchaRef.current?.reset();
      const recaptchaTokenV2 = await recaptchaRef.current?.executeAsync();
      if (!recaptchaTokenV2) {
        track('Invalid Signup reCAPTCHA V2');
        return {};
      }

      const result = await mutate({
        errorPolicy: 'all', // do not raise but rather forward errors through `result.error`
        variables: {
          input: {
            ...rest,
            signupSport: sport,
            password: passwordHash,
            // oxlint-disable-next-line typescript/no-deprecated -- legacy REFERRER links still rely on this; dropping it loses attribution
            referrer: localStorage.getItem('REFERRER'),
            referrerCode: localStorage.getItem('REFERRER_CODE'),
            impactClickId: localStorage.getItem('IMPACT_CLICKID'),
            googleClickId: localStorage.getItem('GOOGLE_CLICKID'),
            facebookClickId: localStorage.getItem('FACEBOOK_CLICKID'),
            twitterClickId: localStorage.getItem('TWITTER_CLICKID'),
            tikTokClickId: localStorage.getItem('TIKTOK_CLICKID'),
            snapchatClickId: localStorage.getItem('SNAPCHAT_CLICKID'),
            redditClickId: localStorage.getItem('REDDIT_CLICKID'),
            utmParams: getParams(),
            fromPath: redirectUrl || afterLoggedInTarget || lastLocation,
            gaClientId: getClientId(),
            recaptchaTokenV2,
            promoCode: signupPromo?.campaignCode,
          },
          sport,
          notificationCategories,
          importantNotificationCategories,
        },
      });

      const errors = result.data?.signUp?.errors || [];
      if (errors.length) {
        if (errors[0].path) {
          // new signup flow display the errors in the form, skip the snack notification
          // if the errors have a path (aka: they are form errors)
          return result;
        }
        showNotification('errors', { errors: formatGqlErrors(errors) });
        return result;
      }

      if (
        CombinedGraphQLErrors.is(result.error) &&
        result.error.errors.some(e => e.message === 'Invalid recaptcha token')
      ) {
        track('Submit invalid signup reCAPTCHA');
      }

      if (!result.error && result.data?.signUp?.currentUser) {
        track(
          'Submit Sign Up Form',
          SubmitSignUpForm.toJSON({
            interactionContext: getInteractionContext(),
          }) as any
        );
        updateQuery(result.data.signUp.currentUser);
        if (!skipOneDayEmailVerificationLatency) {
          /** Redirection happens on RedirectAfterSignin */
          showNotification('signUp', {
            nickname: result.data!.signUp?.currentUser!.nickname,
          });
        }
        setSignupPromo(null);
      }

      return result;
    },
    [
      recaptchaRef,
      mutate,
      getParams,
      redirectUrl,
      afterLoggedInTarget,
      lastLocation,
      signupPromo?.campaignCode,
      track,
      showNotification,
      getInteractionContext,
      updateQuery,
      setSignupPromo,
      sport,
      skipOneDayEmailVerificationLatency,
    ]
  );

  return submit;
};
